import Model, { Integer } from 'src/models/model'

export function combinePnlData (pnlDataArray) {
  const combinedPnlData = {}
  _.each(pnlDataArray, pnlData => {
    _.each(pnlData, (value, key) => {
      if (!_.has(combinedPnlData, key)) {
        combinedPnlData[key] = 0
      }
      combinedPnlData[key] += value
    })
  })
  return combinedPnlData
}

export function profitabilityDisplayFormat (profitabilityMode) {
  if (profitabilityMode === 'per-cap' || profitabilityMode === 'ticket-price') {
    return 'currency'
  } else if (profitabilityMode === 'demand' || profitabilityMode === 'attendance') {
    return 'decimal0'
  } else {
    return 'currencyNoCents'
  }
}

export const PNL_CONFIG_KEYS = {
  AVERAGE_LABOR_RATE_CENTS: 'average_labor_rate_cents',
  AVERAGE_LABOR_SECONDS_PER_ATTENDEE: 'average_labor_seconds_per_attendee',
  ANNUAL_SEAT_HOURS: 'annual_seat_hours',
  LABOR_MODE: 'labor_mode',
  MONTHLY_VARIABLE_OVERHEAD_CENTS: 'monthly_variable_overhead_cents',
  MONTHLY_FIXED_OVERHEAD_CENTS: 'monthly_fixed_overhead_cents',
  OTHER_SHOWING_COSTS_CENTS: 'other_showing_costs_cents',
  OTHER_SHOWING_HOUR_COSTS_CENTS: 'other_showing_hour_costs_cents',
  OTHER_SEAT_HOUR_COSTS_CENTS: 'other_seat_hour_costs_cents',
  OTHER_TICKET_COSTS_CENTS: 'other_ticket_costs_cents',
  LABOR_MODE_AVERAGE: 'average'
}

export default class PnlData extends Model {
  static name = 'PnlData'

  static noId = true

  static define () {
    this.fields = {
      showingCount: {
        type: Integer
      },

      ticketsSold: {
        type: Integer
      },

      boxOfficeRetailNet: {
        type: Number
      },
      boxOfficeRevenue: {
        type: Number
      },
      boxOfficeNetReported: {
        type: Number
      },
      boxOfficeGrossPaid: {
        type: Number
      },
      boxOfficeGrossReported: {
        type: Number
      },
      terms: {
        type: Number
      },
      boxOfficeCost: {
        type: Number
      },

      emptyCount: {
        type: Integer
      },

      refundCount: {
        type: Integer
      },
      complimentaryCount: {
        type: Integer
      },

      concessionCount: {
        type: Number
      },
      concessionRevenue: {
        type: Number
      },
      concessionCost: {
        type: Number
      },

      seatCount: {
        type: Number
      },
      durationHours: {
        type: Number
      },

      allocatedLaborHours: {
        type: Number
      }
    }

    this.computed = {
      revenue () {
        return this.boxOfficeRevenue + this.concessionRevenue
      },
      cost () {
        return this.boxOfficeCost + this.concessionCost
      },

      concessionGrossProfit () {
        return this.concessionRevenue - this.concessionCost
      },
      boxOfficeGrossProfit () {
        return this.boxOfficeRevenue - this.boxOfficeCost
      },

      grossProfit () {
        return this.concessionGrossProfit + this.boxOfficeGrossProfit
      },

      averageLaborHours () {
        return this.ticketsSold * this.pnlConfigValue(PNL_CONFIG_KEYS.AVERAGE_LABOR_SECONDS_PER_ATTENDEE) / 3600
      },
      averageLaborCost () {
        return this.averageLaborHours * this.pnlConfigValue(PNL_CONFIG_KEYS.AVERAGE_LABOR_RATE_CENTS)
      },

      allocatedLaborCost () {
        return this.allocatedLaborHours * this.pnlConfigValue(PNL_CONFIG_KEYS.AVERAGE_LABOR_RATE_CENTS)
      },

      laborHours () {
        return this.pnlConfigValue(PNL_CONFIG_KEYS.LABOR_MODE) === PNL_CONFIG_KEYS.LABOR_MODE_AVERAGE ? this.averageLaborHours : this.allocatedLaborHours
      },

      laborCost () {
        return this.pnlConfigValue(PNL_CONFIG_KEYS.LABOR_MODE) === PNL_CONFIG_KEYS.LABOR_MODE_AVERAGE ? this.averageLaborCost : this.allocatedLaborCost
      },

      postLaborProfit () {
        return this.grossProfit - this.laborCost
      },

      variableOverheadCost () {
        const annualVariableOverheadCostCents = this.pnlConfigValue(PNL_CONFIG_KEYS.MONTHLY_VARIABLE_OVERHEAD_CENTS) * 12
        const variableOverheadCostPerSeatHour = annualVariableOverheadCostCents / this.pnlConfigValue(PNL_CONFIG_KEYS.ANNUAL_SEAT_HOURS)
        return variableOverheadCostPerSeatHour * this.averageSeatCount * this.averageDurationHours * this.showingCount
      },

      otherShowingCosts () {
        return this.pnlConfigValue(PNL_CONFIG_KEYS.OTHER_SHOWING_COSTS_CENTS)
      },
      otherShowingHourCosts () {
        return this.pnlConfigValue(PNL_CONFIG_KEYS.OTHER_SHOWING_HOUR_COSTS_CENTS) * this.durationHours
      },
      otherSeatHourCosts () {
        return this.pnlConfigValue(PNL_CONFIG_KEYS.OTHER_SEAT_HOUR_COSTS_CENTS) * this.averageSeatCount * this.averageDurationHours * this.showingCount
      },
      otherTicketCosts () {
        return this.pnlConfigValue(PNL_CONFIG_KEYS.OTHER_TICKET_COSTS_CENTS) * this.ticketsSold
      },

      variableCost () {
        return this.variableOverheadCost +
          this.otherShowingCosts +
          this.otherShowingHourCosts +
          this.otherSeatHourCosts +
          this.otherTicketCosts
      },

      contributionMargin () {
        return this.postLaborProfit - this.variableCost
      },

      fixedOverheadCost () {
        const annualFixedOverheadCostCents = this.pnlConfigValue(PNL_CONFIG_KEYS.MONTHLY_FIXED_OVERHEAD_CENTS) * 12
        const fixedOverheadCostPerSeatHour = annualFixedOverheadCostCents / this.pnlConfigValue(PNL_CONFIG_KEYS.ANNUAL_SEAT_HOURS)
        return fixedOverheadCostPerSeatHour * this.averageSeatCount * this.averageDurationHours * this.showingCount
      },

      operatingProfit () {
        return this.contributionMargin - this.fixedOverheadCost
      },

      averageDurationHours () {
        return this.durationHours / this.showingCount
      },

      averageSeatCount () {
        return this.seatCount / this.showingCount
      },

      variableOverheadRate () {
        return this.variableOverheadCost / this.averageDurationHours / this.averageSeatCount / this.showingCount
      },

      fixedOverheadRate () {
        return this.fixedOverheadCost / this.averageDurationHours / this.averageSeatCount / this.showingCount
      }
    }
  }

  get pnlConfig () {
    return this.configuration.currentSite.pnlConfig
  }

  pnlConfigValue (key) {
    const value = this.pnlConfig[key]

    return value?.override ? value.custom : (value?.computed || value)
  }

  profitabilityDisplayValue (profitabilityMode) {
    switch (profitabilityMode) {
      case 'demand':
        return this.ticketsSold
      case 'attendance':
        return this.ticketsSold
      case 'revenue':
        return this.revenue / 100
      case 'per-cap':
        return this.concessionRevenue / (this.ticketsSold || 1) / 100
      case 'ticket-price':
        return this.boxOfficeRevenue / (this.ticketsSold || 1) / 100
      case 'gross-profit':
        return this.grossProfit / 100
      case 'post-labor-profit':
        return this.postLaborProfit / 100
      case 'contribution-margin':
        return this.contributionMargin / 100
      case 'operating-profit':
        return this.operatingProfit / 100
      default:
        throw new Error('Unhandled profitabilityMode: ' + profitabilityMode)
    }
  }

  static dataFromShowingMetrics (showingMetrics) {
    const pnlData = {}

    if (!showingMetrics) {
      return pnlData
    }

    pnlData.showingCount = showingMetrics.showing_count || 1
    pnlData.ticketsSold = showingMetrics.tickets_sold

    pnlData.boxOfficeRetailNet = showingMetrics.box_office_retail_net
    pnlData.boxOfficeRevenue = showingMetrics.box_office_revenue
    pnlData.boxOfficeNetReported = showingMetrics.box_office_net_reported
    pnlData.boxOfficeGrossPaid = showingMetrics.box_office_gross_paid
    pnlData.boxOfficeGrossReported = showingMetrics.box_office_gross_reported
    pnlData.boxOfficeCost = showingMetrics.box_office_cost

    pnlData.emptyCount = showingMetrics.empty_count
    pnlData.refundCount = showingMetrics.refund_count
    pnlData.concessionCount = showingMetrics.concession_count

    pnlData.concessionRevenue = showingMetrics.concession_revenue
    pnlData.concessionCost = showingMetrics.concession_cost

    pnlData.seatCount = showingMetrics.seat_count
    pnlData.durationHours = showingMetrics.duration_hours
    pnlData.allocatedLaborHours = showingMetrics.allocated_labor_hours

    return pnlData
  }

  static fromShowingMetrics (showingMetrics) {
    return new PnlData(this.dataFromShowingMetrics(showingMetrics))
  }
}
